import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomFieldsForm from '../components/CustomFieldsForm';
import AssetPicker from '../components/AssetPicker';
import { useMe } from '../lib/me';
import { contractorAuth, portalContractor } from '../lib/contractorAuth';
import useUnsavedChanges, { UnsavedChangesDialog } from '../lib/useUnsavedChanges';
import { approverOption, awayPhrase } from '../lib/people';
import HelpSuggestions from '../components/help/HelpSuggestions';
import { helpApi } from '../lib/help';
import { useFeature } from '../lib/features';
import './CreateTicket.css';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Create Ticket - the one form, for everybody who raises a ticket.
 *
 * A member of staff, an administrator (for themselves or for somebody else),
 * and - since 26 October - a contractor. Gordon: "On the contractor's view,
 * when a contractor raises a request, the request can be linked to an asset
 * as well - why don't we have the standard ticket here? It's more open." So
 * the contractor's own form (pages/ContractorCreateTicket.js, 22 October)
 * is retired and this one serves them in the portal's frame.
 *
 * WHOSE FORM IT IS is decided once, from lib/contractorAuth's
 * portalContractor(): a contractor on a portal token, or an administrator
 * looking through View as at a contractor, gets the PORTAL shape; anybody
 * else the staff shape. The two differ only where a request to the firm
 * differs from a staff ticket:
 *
 *   requester     staff: themselves, or anybody for an administrator.
 *                 portal: the contractor, read-only - a request is theirs.
 *   department    staff only. A request chooses no department.
 *   templates     staff only; the endpoint is staff's.
 *   device        both. For a contractor the picker lists what they hold
 *                 or look after and nothing else (components/AssetPicker,
 *                 GET /assets/picker), and the server refuses anything
 *                 outside that (lib/asset-link.js pickableFor).
 *   related job   portal only: the ticket of theirs this is in connection
 *                 with, when they have any (migration 064).
 *
 * and in what the POST carries: the portal form never sends requester_id,
 * department_id, assigned_to, contractor_id or status_id - the server refuses
 * them by name for a contractor (routes/tickets-core.js STAFF_ONLY) - and
 * under View as names the contractor in requester_contractor_id, because an
 * administrator holds no portal token. Afterwards a contractor lands on their
 * own ticket page (/my-tickets/:id) with the "Sent for approval" line; staff
 * and administrators on the staff page.
 *
 * ?title= and ?description= start the form worded and ?asset= preselects a
 * device - "Report a problem" and "This is not mine" on My Equipment use all
 * three, for a contractor as for staff. Only into empty fields, once, so
 * nothing typed is ever overwritten by the address bar.
 */
function CreateTicket() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API_URL = process.env.REACT_APP_API_URL;

  // The contractor whose form this is, or null for the staff shape.
  const who = useMemo(() => portalContractor(), []);
  const portal = Boolean(who?.id);
  // "Try these first" follows Help's own switch (3 November).
  const helpOn = useFeature('help');
  // Their portal token; undefined for staff and under View as, where the
  // app's own interceptor signs the call with the administrator's token.
  const auth = useMemo(() => contractorAuth(), []);
  // Headers for a multipart upload, with the token kept when there is one.
  const uploadConfig = (extra) => ({
    ...(auth || {}),
    headers: { ...(auth?.headers || {}), ...extra }
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requester_id: '',
    priority_id: 2,
    department_id: '',
    category_id: '',
    approver_id: '',
    related_ticket_id: ''
  });

  // The device the ticket is about, if any. Held as the whole object rather
  // than an id so the chosen card can show its tag and model without asking
  // the server again. ?asset=<id> preselects one - "Report a problem" on a
  // device's page lands here with it chosen.
  const [device, setDevice] = useState(null);
  const askedForDevice = searchParams.get('asset');

  useEffect(() => {
    const t = searchParams.get('title');
    const d = searchParams.get('description');
    if (!t && !d) return;
    setFormData((f) => ({
      ...f,
      title: f.title || (t ? t.slice(0, 200) : ''),
      description: f.description || (d ? d.slice(0, 4000) : '')
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Who can approve, and who the server says is filling this form in.
  const [approvers, setApprovers] = useState([]);

  const me = useMe();

  // The requester is whoever is filling the form in. It used to be a dropdown
  // of everybody, which meant picking your own name from a list of forty and
  // being able to raise a ticket as someone else.
  useEffect(() => {
    if (!portal && me?.signedIn && me.id) {
      setFormData((f) => (f.requester_id ? f : { ...f, requester_id: me.id }));
    }
  }, [me, portal]);

  // Whether the firm insists on an approver. Defaults to insisting, and stays
  // insisting if the read fails - the same way the server does - because a
  // form that quietly stops asking is how a ticket ends up in no queue.
  const [approverRequired, setApproverRequired] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/auth/approvers`, auth)
      .then((r) => { if (live) setApprovers(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setApprovers([]); });
    axios.get(`${API_URL}/app-settings`, auth)
      .then((r) => { if (live) setApproverRequired(r.data?.require_approver !== false); })
      .catch(() => { if (live) setApproverRequired(true); });
    return () => { live = false; };
  }, [API_URL, auth]);

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  // The jobs a contractor is on, for "Related to one of my tickets".
  const [jobs, setJobs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [dragging, setDragging] = useState(false);
  // The server's own reason for refusing, shown on the page. It used to be
  // alert('Please try again'), which discarded it.
  const [submitError, setSubmitError] = useState(null);
  // Answers to the category's own fields, collected before the ticket exists
  // and written immediately after it does.
  const [customValues, setCustomValues] = useState({});
  const [referenceData, setReferenceData] = useState({
    departments: [],
    categories: [],
    priorities: [],
    users: []
  });

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  /*
   * NOTHING TYPED IS THROWN AWAY WITHOUT ASKING (lib/useUnsavedChanges).
   * Nine hundred lines of form and no guard: a browser Back, a bell link or
   * a closed tab discarded everything, and the window-level drop guard in
   * App.js exists because exactly that cost Gordon a description once.
   * "Dirty" is what a person typed or chose - a title, a description, a
   * category, a related job, files, answers to the category's fields - and
   * not what the page filled in for them (the requester, the default
   * priority, the approver). Released the moment the ticket exists.
   */
  // A title or description that came in the address ("Report a problem" on a
  // device) is the page's wording, not the person's, until they change it.
  const givenTitle = (searchParams.get('title') || '').slice(0, 200).trim();
  const givenDescription = (searchParams.get('description') || '').slice(0, 4000).trim();
  const typedTitle = String(formData.title || '').trim();
  const typedDescription = String(formData.description || '').trim();
  const dirty = !loading && Boolean(
    (typedTitle && typedTitle !== givenTitle)
    || (typedDescription && typedDescription !== givenDescription)
    || formData.category_id || formData.related_ticket_id || attachments.length > 0
    || Object.values(customValues).some((v) => v !== '' && v !== null && v !== undefined));
  const leave = useUnsavedChanges(dirty, { what: 'This ticket' });

  /*
   * The lists the form is drawn from. Staff read /reference-data, one call
   * with everything the staff shape needs. A contractor cannot - the staff
   * directory is not theirs - so the portal shape reads the two lists that
   * are (categories, priorities), each its own request and its own failure:
   * a form that cannot learn the categories is still a form that can take a
   * title. Priorities default to Medium by NAME; the ids are a serial and
   * nothing here should remember one.
   */
  const fetchReferenceData = useCallback(async () => {
    setDataLoading(true);
    const soft = (path) => axios.get(`${API_URL}${path}`, auth)
      .then((r) => (Array.isArray(r.data) ? r.data : []))
      .catch(() => []);
    try {
      if (portal) {
        const [categories, priorities, assignments] = await Promise.all([
          soft('/categories'), soft('/priorities'),
          soft(`/contractors/${who.id}/assignments`)
        ]);
        setReferenceData({ departments: [], categories, priorities, users: [] });
        // Only the jobs they are DOING. Since 064 the same list carries the
        // requests they raised, and a request about a request is not a thing.
        setJobs(assignments.filter((t) => t.assigned_to_me !== false));
        const medium = priorities.find((p) => /^medium$/i.test(p.name))
          || priorities[Math.floor(priorities.length / 2)];
        if (medium) setFormData((f) => ({ ...f, priority_id: medium.id }));
        return;
      }
      const response = await axios.get(`${API_URL}/reference-data`);
      // Merge rather than replace: a payload missing any key used to leave
      // that array undefined, and the .map below took the whole page down.
      const d = response.data || {};
      setReferenceData({
        departments: Array.isArray(d.departments) ? d.departments : [],
        categories:  Array.isArray(d.categories)  ? d.categories  : [],
        priorities:  Array.isArray(d.priorities)  ? d.priorities  : [],
        users:       Array.isArray(d.users)       ? d.users       : []
      });
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setSubmitError('Failed to load form options. Please refresh the page.');
    } finally {
      setDataLoading(false);
    }
  }, [API_URL, auth, portal, who]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  /*
   * "TRY THESE FIRST" (Help, 123) - staff only: the Help articles the title
   * suggests (components/help/HelpSuggestions). An article opened from there
   * and then the form LEFT without a ticket is "solved without a ticket":
   * one POST /help/deflections as the page goes - by the router, or with the
   * tab - naming every article opened. The server counts each once per
   * person per article per day. Raising the ticket means it was not solved,
   * so nothing is sent.
   */
  const helpOpened = useRef(new Set());
  const ticketRaised = useRef(false);
  const noteHelpOpened = useCallback((id) => { helpOpened.current.add(id); }, []);
  useEffect(() => {
    if (portal) return undefined;
    let sent = false;
    const report = () => {
      if (sent || ticketRaised.current || helpOpened.current.size === 0) return;
      sent = true;
      helpApi.post('/help/deflections', { node_ids: [...helpOpened.current] }).catch(() => {});
    };
    window.addEventListener('pagehide', report);
    return () => { window.removeEventListener('pagehide', report); report(); };
  }, [portal]);

  // Templates are optional furniture: if the endpoint is unavailable the form
  // still works, it just has nothing to prefill from. Staff only - the
  // endpoint is staff's, and a request is not a runbook.
  useEffect(() => {
    if (portal) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/templates`)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : (res.data?.templates ?? []);
        setTemplates(rows.filter((t) => t.is_active !== false));
      })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [API_URL, portal]);

  // A template picked from the Templates page arrives as ?template=<id>.
  const applyTemplate = useCallback((id) => {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => String(x.id) === String(id));
    if (!t) return;
    const fields = t.template_fields || {};
    const steps = Array.isArray(fields.steps) ? fields.steps : [];
    const body = [
      fields.summary || t.description || '',
      steps.length ? '' : null,
      steps.length ? 'Checklist:' : null,
      ...steps.map((step, i) => `${i + 1}. ${step}`)
    ].filter((line) => line !== null).join('\n');

    // Only prefill what is empty, so picking a template never silently wipes
    // something already typed.
    setFormData((prev) => ({
      ...prev,
      title: prev.title || t.name || '',
      description: prev.description || body,
      category_id: prev.category_id || t.category_id || '',
      priority_id: t.priority_id || prev.priority_id
    }));
  }, [templates]);

  useEffect(() => {
    const fromUrl = searchParams.get('template');
    if (fromUrl && templates.length && !templateId) applyTemplate(fromUrl);
  }, [searchParams, templates, templateId, applyTemplate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * One place that accepts files, whichever way they arrived.
   *
   * The button and the drop zone both come through here, so the size limit and
   * the de-duplication cannot drift between them - which is how a drop zone
   * ends up accepting a file the picker refuses.
   */
  const addFiles = useCallback((incoming) => {
    const files = Array.from(incoming || []);
    if (files.length === 0) return;

    const rejected = [];
    const accepted = [];
    files.forEach((file) => {
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} is ${(file.size / 1048576).toFixed(1)}MB — the limit is 10MB`);
        return;
      }
      // A folder dragged in arrives as a zero-byte entry with no type, and the
      // upload would be refused by the server after the ticket was already
      // created. Said here instead.
      if (file.size === 0) {
        rejected.push(`${file.name} is empty, or is a folder rather than a file`);
        return;
      }
      accepted.push(file);
    });

    setAttachments((prev) => {
      // Dropping the same file twice is easy to do and there is no reason to
      // upload it twice.
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const fresh = accepted.filter((f) => !seen.has(`${f.name}:${f.size}`));
      accepted.forEach((f) => {
        if (seen.has(`${f.name}:${f.size}`)) rejected.push(`${f.name} is already attached`);
      });
      return [...prev, ...fresh];
    });
    setFileErrors(rejected);
  }, []);

  const handleFileSelect = (e) => {
    addFiles(e.target.files);
    // Reset the input, so choosing the same file again still fires onChange.
    e.target.value = '';
  };

  /**
   * Drag and drop, which the box has claimed to support since it was written.
   *
   * It said "Click to select files or drag & drop" above a dashed border and
   * had no drop handler at all - and a page with no drop handler does not
   * ignore the file, it lets the BROWSER handle it, which means navigating the
   * tab to the file itself. The React app unmounts, and everything typed into
   * the form goes with it. That is why a dropped attachment and a typed
   * description disappeared together: one cause, two symptoms.
   *
   * preventDefault on dragOver is what claims the drop. Without it the drop
   * event never reaches this element at all, whatever is bound to it.
   */
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // relatedTarget is where the pointer went. Moving between the box and its
    // own label fires dragleave on the box, so without this check the
    // highlight flickers the whole time a file is held over it.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setFileErrors([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Checked here, by name, because the server requires all three and used to
    // answer a missing description with a 400 that this page reported as
    // "Failed to create ticket. Please try again." - which named no field, so
    // trying again did exactly the same thing.
    const missing = [];
    if (!String(formData.title || '').trim()) missing.push('a title');
    if (!String(formData.description || '').trim()) missing.push('a description');
    if (!portal && !formData.requester_id) missing.push('who it is for');
    if (!formData.approver_id && approverRequired) missing.push('an approver');
    if (missing.length > 0) {
      setSubmitError(`This ticket still needs ${missing.join(', ')}.`);
      return;
    }

    try {
      setLoading(true);
      setSubmitError(null);
      const payload = {
        title: formData.title,
        description: formData.description,
        priority_id: parseInt(formData.priority_id),
        category_id: formData.category_id || null,
        approver_id: formData.approver_id || null,
        // Only sent when one was chosen, so a server that has not run the
        // register's migrations never sees the field at all.
        ...(device ? { asset_id: device.id } : {}),
        ...(portal
          ? {
            // A contractor's request: theirs, about one of their jobs if
            // they said so, and - under View as, where there is no portal
            // token - naming the contractor. The server takes that from an
            // administrator only (routes/tickets-core.js).
            related_ticket_id: formData.related_ticket_id || null,
            ...(who.viewing ? { requester_contractor_id: who.id } : {})
          }
          : {
            requester_id: formData.requester_id,
            department_id: formData.department_id || null
          })
        // No status_id. This sent the literal 1, commented "Default to Open
        // status" - and 1 is Awaiting Approval, not Open, so the comment was
        // wrong and the form was right by accident. Anyone correcting the one
        // to match the other would have started every new ticket past the
        // approval queue it is supposed to enter.
        //
        // The server decides, and looks the status up by name.
      };

      const response = await axios.post(`${API_URL}/tickets`, payload, auth);
      const ticketId = response.data.id;
      // A ticket after all: whatever Help suggested did not solve it.
      ticketRaised.current = true;

      // The category's own fields, now that there is a ticket to hang them
      // on. Failures are collected rather than thrown: the ticket exists, and
      // losing it over an unsaved extra field would be the worse outcome.
      const fieldProblems = [];
      for (const [fieldId, value] of Object.entries(customValues)) {
        if (value === '' || value === null || value === undefined) continue;
        try {
          await axios.post(`${API_URL}/tickets/${ticketId}/custom-field-values`,
            { custom_field_id: Number(fieldId), value }, auth);
        } catch (err) {
          fieldProblems.push(err.response?.data?.error || 'a field could not be saved');
        }
      }

      // Upload attachments if any. A failure here used to be swallowed, which
      // mattered less when the endpoint was a stub that discarded everything
      // anyway - now that it really stores files, a file that did not make it
      // has to be named rather than lost quietly.
      const failed = [];
      for (const file of attachments) {
        const formDataFile = new FormData();
        formDataFile.append('file', file);
        formDataFile.append('fileName', file.name);
        formDataFile.append('fileType', file.type);
        formDataFile.append('fileSize', file.size);

        try {
          await axios.post(
            `${API_URL}/tickets/${ticketId}/attachments`,
            formDataFile,
            uploadConfig({ 'Content-Type': 'multipart/form-data' })
          );
        } catch (attachmentError) {
          console.error('Error uploading attachment:', attachmentError);
          failed.push({
            name: file.name,
            reason: attachmentError.response?.data?.error || 'upload failed'
          });
        }
      }

      // What did not make it, carried to the ticket page and said there -
      // these were two alert() calls to dismiss before the page moved on.
      const notices = [];
      if (fieldProblems.length > 0) {
        notices.push(
          'The ticket was created, but some of the extra fields for this '
          + `category did not save: ${fieldProblems.join('; ')}. `
          + 'You can fill them in again from the ticket.'
        );
      }

      if (portal) {
        // A contractor goes to their own page for it, with a calm word that
        // it is with the approver and the files that did not make it named
        // there.
        leave.release();
        // Their own page for it, under View as too: /my-tickets/:id draws
        // the contractor's page for both (App.js).
        navigate(`/my-tickets/${ticketId}`, {
          state: {
            sent: true,
            ticketNumber: response.data.ticket_number,
            filesFailed: failed.map((f) => `${f.name} — ${f.reason}`),
            ...(notices.length ? { notice: notices.join(' ') } : {})
          }
        });
        return;
      }

      if (failed.length > 0) {
        // The ticket itself was created, so send them to it either way - they
        // can retry the files from the ticket's own attachments panel.
        notices.push(
          `The ticket was created, but ${failed.length} file(s) did not upload: ` +
          failed.map((f) => `${f.name} — ${f.reason}`).join('; ') +
          '. You can add them again from the ticket.'
        );
      }

      leave.release();
      navigate(`/tickets/${ticketId}`, notices.length ? { state: { notice: notices.join(' ') } } : undefined);
    } catch (error) {
      console.error('Error creating ticket:', error);
      // The server's own words. It says things worth reading - which field is
      // missing, that the database is unavailable, which migration a server
      // still needs - and all of it was being replaced with "Please try
      // again". Shown on the page rather than in an alert, so the form is
      // still there to correct underneath it.
      const body = error.response?.data || {};
      setSubmitError(
        [body.error, body.detail].filter(Boolean).join(' ')
        || (error.response
          ? `The server refused this ticket (${error.response.status}).`
          : 'Could not reach the server. The ticket was not created.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => leave.guard(() => {
    if (portal) navigate(who.viewing ? '/contractor-dashboard' : '/my-requests');
    else navigate('/');
  });

  if (dataLoading) {
    return <div className="container ct-page"><p>Loading form...</p></div>;
  }

  return (
    <div className="container ct-page" data-shape={portal ? 'portal' : 'staff'}>
      <div className="page-header ct-head">
        <div>
          <h1 className="page-title">Create Ticket</h1>
          <p className="page-description">
            {portal
              ? 'A request to the firm — something to buy, a subscription, access, or a problem with a device you look after. It goes to an approver first.'
              : 'Submit a new IT support request'}
          </p>
        </div>
      </div>

      {portal ? (
        who.viewing ? (
          <p className="ct-viewing">
            Raising this as <strong>{who.name}</strong>, who will be named as the
            requester. You are recorded as the person who entered it.
          </p>
        ) : (
          <p className="ct-portal-note">
            You will see it under Tickets &rsaquo; My requests, and your bell will tell you when it is decided.
          </p>
        )
      ) : (
        <div className="info-banner">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"
               strokeLinecap="round" aria-hidden="true" className="ct-info-glyph">
            <circle cx="9" cy="9" r="6.8" /><path d="M9 8.2v4.2M9 5.6h.01" />
          </svg>
          <span>Please provide detailed information about your issue. This helps our team resolve it faster.</span>
        </div>
      )}

      <UnsavedChangesDialog leave={leave} />
      <form className="form-container ct-form" onSubmit={handleSubmit}>
        {submitError && <p className="ct-error" role="alert">{submitError}</p>}
        {/* The template picker was the first thing on this form, with a label
            and two lines of help text, so the page opened by asking a question
            almost nobody answers. It is a shortcut, not a step: one quiet line
            above the fields, titled rather than explained. */}
        {templates.length > 0 && (
          <div className="ct-template">
            <label htmlFor="template">Start from a template</label>
            <select
              id="template"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              disabled={loading}
              title="Fills in the title, checklist, category and priority. Anything you have already typed is left alone."
            >
              <option value="">None</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group ct-lead">
          <label htmlFor="title">
            Ticket title <span className="required">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            placeholder={portal
              ? 'e.g. Adobe Acrobat licence, a replacement PSU, the screen on IT-MON-000201'
              : 'Brief description of the issue'}
            value={formData.title}
            onChange={handleChange}
            required
            maxLength={200}
            disabled={loading}
          />
          <div className="help-text">Keep it concise and descriptive</div>
          {!portal && helpOn && <HelpSuggestions title={formData.title} onOpened={noteHelpOpened} />}
        </div>

        <div className="form-group ct-lead">
          <label htmlFor="description">
            Description <span className="required">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            placeholder={portal
              ? 'Say what it is, why the work needs it, and roughly what it costs if you know.'
              : 'What happened? When did it start? Any error messages?'}
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
            rows="5"
          />
          <div className="help-text">
            {portal ? 'A quote, a model number or a screenshot can go on as a file below' : 'Include steps to reproduce if possible'}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="requester_id">
            Your Name <span className="required">*</span>
          </label>
          {portal ? (
            <>
              <input
                id="requester_id"
                type="text"
                value={who.name || 'Contractor'}
                readOnly
                disabled
              />
              <div className="help-text">
                {who.viewing
                  ? 'Named as the requester; you are recorded as the person who entered it.'
                  : 'Taken from your portal sign-in. The request is filed as yours.'}
              </div>
            </>
          ) : me?.isAdmin ? (
            <>
              <select
                id="requester_id"
                name="requester_id"
                value={formData.requester_id}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select a requester</option>
                {referenceData.users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </option>
                ))}
              </select>
              <div className="help-text">
                Defaults to you. Change it to raise this on someone else&rsquo;s behalf.
              </div>
            </>
          ) : (
            <>
              <input
                id="requester_id"
                type="text"
                value={me?.name || me?.email || ''}
                readOnly
                disabled
              />
              <div className="help-text">Taken from your sign-in</div>
            </>
          )}
        </div>

        {/* WHICH DEVICE. After the requester because the picker offers the
            REQUESTER's devices - an administrator raising this for Rita sees
            Rita's laptop; a contractor sees what they hold or look after.
            Renders nothing at all while the register is off. */}
        <AssetPicker
          asFormGroup
          label="Device"
          value={device}
          onChange={setDevice}
          requesterId={portal ? null : (formData.requester_id || null)}
          contractor={portal ? who : null}
          initialId={askedForDevice}
        />

        <div className="form-group">
          <label htmlFor="approver_id">
            Approver {approverRequired && <span className="required">*</span>}
          </label>
          <select
            id="approver_id"
            name="approver_id"
            value={formData.approver_id}
            onChange={handleChange}
            disabled={loading}
          >
            {/* "No approval needed" used to be the first option and the
                default, so a ticket raised without thinking entered no
                approval queue and waited on a decision nobody had been asked
                to make. An approver is required unless an administrator has
                turned that off in Settings - and even then the wording below
                says what happens instead, rather than offering "no approval"
                as though the ticket skips the queue. */}
            <option value="">
              {approverRequired ? 'Choose an approver…' : 'Choose an approver, or leave for anyone'}
            </option>
            {/* An approver who is away (113) says so, and who decides
                meanwhile - still nameable: the delegate decides it on
                their behalf, and it is theirs again when they are back. */}
            {approvers.map((a) => (
              <option key={a.id} value={a.id}>{approverOption(a)}</option>
            ))}
          </select>
          {(() => {
            const chosen = approvers.find((a) => a.id === formData.approver_id);
            return chosen?.away?.now ? (
              <div className="help-text ct-away" data-approver-away="">
                {chosen.display_name || chosen.email} is {awayPhrase(chosen.away)}.
                {chosen.away.delegate_name ? ` ${chosen.away.delegate_name} will decide this on their behalf.` : ''}
              </div>
            ) : null;
          })()}
          <div className="help-text">
            {approvers.length === 0
              ? (portal
                ? 'Nobody is available to approve yet. Ask the office who signs these off.'
                : 'Nobody is available to approve yet — an administrator has to sign in once, and be ticked as an approver in Settings, before they can be named here.')
              : approverRequired
                ? 'Who signs this off before work starts. Every ticket needs one.'
                : 'Who signs this off before work starts. Leave it blank and it goes to whichever approver picks it up — it still waits for one.'}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group ct-half">
            <label htmlFor="priority_id">Priority</label>
            <select
              id="priority_id"
              name="priority_id"
              value={formData.priority_id}
              onChange={handleChange}
              disabled={loading}
            >
              {referenceData.priorities.map(priority => (
                <option key={priority.id} value={priority.id}>
                  {priority.name}
                </option>
              ))}
            </select>
            <div className="help-text">
              {portal ? 'High and Critical reach the approver straight away.' : 'How urgent is this issue?'}
            </div>
          </div>

          <div className="form-group ct-half">
            <label htmlFor="category_id">Category</label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select category</option>
              {referenceData.categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <div className="help-text">{portal ? 'Purchase, Subscription, or the kind of fault' : 'Type of issue'}</div>
          </div>
        </div>

        {/* Renders nothing at all unless this category actually has extra
            fields. There is no ticket to save against yet, so the answers are
            held here and written once the ticket has an id. */}
        {formData.category_id && (
          <CustomFieldsForm
            ticketCategory={
              referenceData.categories.find(c => String(c.id) === String(formData.category_id))?.name || ''
            }
            ticketId={null}
            onValuesChanged={setCustomValues}
            auth={auth}
          />
        )}

        {!portal && (
          <div className="form-group">
            <label htmlFor="department_id">Department</label>
            <select
              id="department_id"
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select department (optional)</option>
              {referenceData.departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            <div className="help-text">Which department does this affect?</div>
          </div>
        )}

        {/* Only when there is something to point at: a contractor with no
            jobs is not asked a question with no answers. */}
        {portal && jobs.length > 0 && (
          <div className="form-group">
            <label htmlFor="related_ticket_id">Related to one of my tickets</label>
            <select id="related_ticket_id" name="related_ticket_id" value={formData.related_ticket_id}
                    onChange={handleChange} disabled={loading}>
              <option value="">Not about a particular job</option>
              {jobs.map((t) => (
                <option key={t.id} value={t.id}>{t.ticket_number} &middot; {t.title}</option>
              ))}
            </select>
            <div className="help-text">Optional. If this is a part or a licence for a job you are on, say which.</div>
          </div>
        )}

        {/* File Attachments Section */}
        <div className="form-group ct-files">
          <label htmlFor="attachments">Attachments (Optional)</label>
          <div
            className={dragging ? 'ct-drop is-dragging' : 'ct-drop'}
            onDragEnter={onDragOver}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              type="file"
              id="attachments"
              multiple
              onChange={handleFileSelect}
              disabled={loading}
              className="ct-drop-input"
            />
            <label htmlFor="attachments">
              <svg className="ct-drop-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13.5 6.5 7.6 12.4a1.9 1.9 0 0 0 2.7 2.7l6.4-6.4a3.6 3.6 0 0 0-5.1-5.1L5.1 10.1a5.3 5.3 0 0 0 7.5 7.5l4.6-4.6" />
              </svg>
              <div className="ct-drop-main">
                {dragging ? 'Drop them here' : 'Click to select files or drag & drop'}
              </div>
              <div className="ct-drop-sub">
                Max 10MB per file. Supported: images, documents, PDFs
              </div>
            </label>
          </div>
          {fileErrors.length > 0 && (
            <ul className="ct-file-errors" role="alert">
              {fileErrors.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
          <div className="help-text">
            {portal ? 'A quote, a screenshot or a link in a file' : 'Attach screenshots, error logs, or relevant documents'}
          </div>

          {/* Attached Files List */}
          {attachments.length > 0 && (
            <div className="ct-attached-wrap">
              <h4 className="ct-attached-title">
                Attached Files ({attachments.length})
              </h4>
              {/* Keyed by name and size rather than by index: removing the
                  first of three files renumbered the rest, so React reused the
                  wrong rows and the list showed a file that had been taken
                  off. */}
              <div className="ct-attached">
                {attachments.map((file, index) => (
                  <div key={`${file.name}:${file.size}`} className="ct-file">
                    <div className="ct-file-text">
                      <div className="ct-file-name">{file.name}</div>
                      <div className="ct-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      disabled={loading}
                      aria-label={`Remove ${file.name}`}
                      className="ct-file-remove"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions ct-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Creating...' : '+ Create Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateTicket;
